// =============================================================
// StatusPage — context ring, status, mood, mailbox widgets,
// Whisper admin status, chat history, and search overlay.
// =============================================================

import { useEffect, useMemo, useRef, useState } from 'react'
import { WhisperPanel } from '../components/WhisperPanel'
import { authFetch } from '../lib/auth'
import { renderMarkdown } from '../lib/markdown'
import { useStatusSocket } from '../lib/sockets'
import type { LettersResponse } from '../lib/types'
import type { NativeContentSegment } from '../lib/nativeTurn'

interface StatusPageProps {
  active: boolean
  onOpenGacha: () => void
  onNavigateChat: () => void
}

interface HistoryMessage {
  role: 'human' | 'assistant'
  text: string
  thinking?: string | null
  segments?: NativeContentSegment[] | null
  timestamp: string
}

interface HistoryResponse {
  date: string
  total: number
  page: number
  page_size: number
  messages: HistoryMessage[]
}

interface SearchResult {
  date: string
  timestamp: string
  role: 'human' | 'assistant'
  snippet: string
  match_start: number
}

const RING_CIRCUMFERENCE = 2 * Math.PI * 60 // 376.99...

function HistoryThinkingSegment({ content }: { content: string }) {
  const [open, setOpen] = useState(false)
  return <div className="hist-thinking-wrap">
    <button className="hist-thinking-btn" type="button" onClick={() => setOpen((value) => !value)}>
      💭 {open ? '收起琥珀的脑子' : '偷看琥珀的脑子'}
    </button>
    {open && <div className="hist-thinking-body feed-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }} />}
  </div>
}

function HistoryOrderedSegments({ segments }: { segments: NativeContentSegment[] }) {
  return <>
    {segments.map((segment, index) => segment.kind === 'thinking'
      ? <HistoryThinkingSegment content={segment.content} key={`thinking-${index}`} />
      : <div className="hist-text feed-md" key={`text-${index}`} dangerouslySetInnerHTML={{ __html: renderMarkdown(segment.content) }} />)}
  </>
}

function HistoryMessageBubble({ msg }: { msg: HistoryMessage }) {
  const [thinkingOpen, setThinkingOpen] = useState(false)
  const isUser = msg.role === 'human'
  const orderedSegments = !isUser && Array.isArray(msg.segments)
    ? msg.segments.filter((segment) => segment.content)
    : []

  const timeStr = (() => {
    if (!msg.timestamp) return ''
    try {
      const d = new Date(msg.timestamp)
      return d.toLocaleTimeString('ja-JP', {
        timeZone: 'Asia/Tokyo',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return ''
    }
  })()

  return (
    <div className={`hist-msg-row ${isUser ? 'hist-user' : 'hist-assistant'}`}>
      <div className={`hist-bubble ${isUser ? 'hist-bubble-user' : 'hist-bubble-assistant'}`}>
        {orderedSegments.length > 0
          ? <HistoryOrderedSegments segments={orderedSegments} />
          : <>
            <div className="hist-text feed-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.text) }} />
            {msg.thinking && (
              <div className="hist-thinking-wrap">
                <button
                  className="hist-thinking-btn"
                  type="button"
                  onClick={() => setThinkingOpen((o) => !o)}
                >
                  💭 {thinkingOpen ? '收起琥珀的脑子' : '偷看琥珀的脑子'}
                </button>
                {thinkingOpen && (
                  <div className="hist-thinking-body feed-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.thinking) }} />
                )}
              </div>
            )}
          </>}
        {timeStr && <span className="hist-ts">{timeStr}</span>}
      </div>
    </div>
  )
}

function SearchOverlay({
  open,
  onClose,
  onSelectDate,
}: {
  open: boolean
  onClose: () => void
  onSelectDate: (date: string, timestamp?: string) => void
}) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setQ('')
      setResults([])
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!q.trim()) {
      setResults([])
      return
    }
    debounceRef.current = setTimeout(() => {
      setLoading(true)
      authFetch('/api/native-chat/history/search?q=' + encodeURIComponent(q.trim()))
        .then((r) => r.json())
        .then((d) => {
          setResults(d.results || [])
          setLoading(false)
        })
        .catch(() => setLoading(false))
    }, 500)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [q])

  function highlightSnippet(snippet: string, matchStart: number, qLen: number) {
    const before = snippet.slice(0, matchStart)
    const match = snippet.slice(matchStart, matchStart + qLen)
    const after = snippet.slice(matchStart + qLen)
    return (
      <>
        {before}
        <mark className="search-highlight">{match}</mark>
        {after}
      </>
    )
  }

  if (!open) return null

  return (
    <div className="search-overlay" onClick={onClose}>
      <div className="search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="search-header">
          <span className="search-title">🔍 搜索聊天记录</span>
          <button className="search-close" type="button" onClick={onClose}>
            ✕
          </button>
        </div>
        <input
          ref={inputRef}
          className="search-input"
          type="text"
          placeholder="输入关键词..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="search-results">
          {loading && <div className="search-empty">搜索中...</div>}
          {!loading && q && results.length === 0 && (
            <div className="search-empty">暂无结果</div>
          )}
          {results.map((r, i) => (
            <button
              key={i}
              className="search-result-item"
              type="button"
              onClick={() => {
                onSelectDate(r.date, r.timestamp)
                onClose()
              }}
            >
              <div className="sri-meta">
                <span className="sri-date">{r.date}</span>
                <span className={`sri-role ${r.role === 'human' ? 'sri-user' : 'sri-assistant'}`}>
                  {r.role === 'human' ? '老婆' : '琥珀'}
                </span>
              </div>
              <div className="sri-snippet">
                {highlightSnippet(r.snippet, r.match_start, q.trim().length)}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export function StatusPage({ active, onOpenGacha, onNavigateChat }: StatusPageProps) {
  const { status } = useStatusSocket()
  const [moodFallback, setMoodFallback] = useState<string>('正在思考人生的意义...')
  const [letters, setLetters] = useState<LettersResponse>({})

  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [historyData, setHistoryData] = useState<HistoryResponse | null>(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyPage, setHistoryPage] = useState(0)
  const [scrollToTs, setScrollToTs] = useState<string | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)

  useEffect(() => {
    authFetch('/api/status')
      .then((r) => r.json())
      .then((d) => {
        if (d && typeof d === 'object' && typeof d.mood === 'string' && d.mood) {
          setMoodFallback(d.mood)
        }
      })
      .catch(() => {})
    authFetch('/api/letters')
      .then((r) => r.json())
      .then((d: LettersResponse) => setLetters(d))
      .catch(() => setLetters({}))
  }, [])

  useEffect(() => {
    if (!selectedDate || !historyOpen) return
    setHistoryLoading(true)
    setHistoryData(null)
    setHistoryPage(0)
    authFetch(`/api/native-chat/history/${selectedDate}?page=0&page_size=100`)
      .then((r) => r.json())
      .then((d: HistoryResponse) => {
        setHistoryData(d)
        setHistoryLoading(false)
        if (scrollToTs) {
          setTimeout(() => {
            const el = document.getElementById('msg-' + scrollToTs)
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' })
              el.classList.add('hist-highlight')
              setTimeout(() => el.classList.remove('hist-highlight'), 3000)
            }
            setScrollToTs(null)
          }, 200)
        }
      })
      .catch(() => setHistoryLoading(false))
  }, [selectedDate, historyOpen, scrollToTs])

  function loadMoreHistory() {
    if (!selectedDate || !historyData) return
    const nextPage = historyPage + 1
    setHistoryPage(nextPage)
    authFetch(`/api/native-chat/history/${selectedDate}?page=${nextPage}&page_size=100`)
      .then((r) => r.json())
      .then((d: HistoryResponse) => {
        setHistoryData((prev) =>
          prev
            ? { ...d, messages: [...prev.messages, ...d.messages] }
            : d,
        )
      })
      .catch(() => {})
  }

  function handleDateSelect(ds: string | null) {
    setSelectedDate(ds)
    setHistoryOpen(false)
    setHistoryData(null)
    setHistoryPage(0)
  }

  function handleSearchSelectDate(ds: string, timestamp?: string) {
    setSelectedDate(ds)
    setHistoryOpen(true)
    setScrollToTs(timestamp || null)
  }

  const contextPct = status?.context_pct ?? 0
  const danger = contextPct >= 80
  const ringOffset = useMemo(
    () => RING_CIRCUMFERENCE - (contextPct / 100) * RING_CIRCUMFERENCE,
    [contextPct],
  )

  const statusClass = (() => {
    if (status?.status === 'online') return 'online'
    if (status?.status === 'busy') return 'busy'
    return ''
  })()
  const statusText = (() => {
    if (status?.status === 'offline') return '离线'
    if (status?.status === 'busy') return '忙碌中'
    return '在线'
  })()
  const rate = status?.rate_5h_pct ?? 0
  const mood = status?.mood || moodFallback

  const yuuLetters = letters.yuu || []
  const kebaoLetters = letters.kebao || []
  const yuuUnread = yuuLetters.filter((l) => !l.read).length
  const kebaoUnread = kebaoLetters.filter((l) => !l.read).length

  const hasMoreHistory =
    historyData != null &&
    historyData.messages.length < historyData.total

  return (
    <>
      <div className={`page ${active ? 'active' : ''}`}>
        <div className="page-inner status-page">
          <div className="context-ring">
            <svg viewBox="0 0 140 140" aria-hidden>
              <defs>
                <linearGradient id="amberGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#D97706" />
                  <stop offset="100%" stopColor="#D97757" />
                </linearGradient>
                <linearGradient id="redGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#EF4444" />
                  <stop offset="100%" stopColor="#B91C1C" />
                </linearGradient>
              </defs>
              <circle className="ring-track" cx="70" cy="70" r="60" />
              <circle
                className="ring-fill"
                cx="70"
                cy="70"
                r="60"
                style={{
                  strokeDashoffset: ringOffset,
                  stroke: danger ? 'url(#redGrad)' : 'url(#amberGrad)',
                }}
              />
            </svg>
            <div className="ring-center">
              <div className="ring-emoji" onClick={onOpenGacha} style={{ cursor: 'pointer' }}>
                🧡
              </div>
              <div
                className="ring-pct"
                style={danger ? { color: 'var(--red)' } : undefined}
              >
                {contextPct > 0 ? `${contextPct}%` : '--'}
              </div>
              <div className="ring-label">context</div>
            </div>
          </div>

          <div className="status-name">
            琥珀
            <div className={`status-dot ${statusClass}`} />
          </div>
          <div className="status-label">
            {statusText}
            {rate > 0 && ` · ⚡${rate}%`}
          </div>

          <div className="mood-card">
            <div className="mood-text">{mood}</div>
          </div>

          <div className="mailbox-widgets">
            <button
              type="button"
              className="mailbox-widget"
              onClick={onNavigateChat}
              style={{ border: 'none', textAlign: 'left' }}
            >
              <div className="mw-header">
                <span className="mw-emoji">🍋‍🟩</span>
                <span className="mw-name">柚子の信箱</span>
              </div>
              <div className="mw-count">
                {yuuLetters.length === 0
                  ? '加载失败'
                  : yuuUnread > 0
                  ? `${yuuUnread}封未读`
                  : `${yuuLetters.length}封信`}
              </div>
            </button>
            <button
              type="button"
              className="mailbox-widget"
              onClick={onNavigateChat}
              style={{ border: 'none', textAlign: 'left' }}
            >
              <div className="mw-header">
                <span className="mw-emoji">🧡</span>
                <span className="mw-name">琥珀の信箱</span>
              </div>
              <div className="mw-count">
                {kebaoLetters.length === 0
                  ? '加载失败'
                  : kebaoUnread > 0
                  ? `${kebaoUnread}封未读`
                  : `${kebaoLetters.length}封信`}
              </div>
            </button>
          </div>

          <WhisperPanel active={active} />

          <div className="history-access-card">
            <div className="history-access-copy">
              <strong>💬 聊天记录</strong>
              <span>按日期查看，或搜索旧对话</span>
            </div>
            <div className="history-access-actions">
              <input
                className="history-date-input"
                type="date"
                value={selectedDate ?? ''}
                onChange={(event) => handleDateSelect(event.target.value || null)}
                aria-label="选择聊天记录日期"
              />
              <button
                className="search-icon-btn"
                type="button"
                onClick={() => setSearchOpen(true)}
                aria-label="搜索聊天记录"
              >
                🔍
              </button>
            </div>
          </div>

          {selectedDate && (
            <div className="hist-section">
              <button
                className="hist-toggle"
                type="button"
                onClick={() => setHistoryOpen((o) => !o)}
              >
                <span>💬 {selectedDate} 聊天记录</span>
                <span className="hist-chevron">{historyOpen ? '▲' : '▼'}</span>
              </button>

              {historyOpen && (
                <div className="hist-body">
                  {historyLoading && (
                    <div className="hist-empty">加载中...</div>
                  )}
                  {!historyLoading && historyData && historyData.messages.length === 0 && (
                    <div className="hist-empty">暂无聊天记录</div>
                  )}
                  {!historyLoading && historyData && historyData.messages.map((msg, i) => (
                    <div key={i} id={msg.timestamp ? 'msg-' + msg.timestamp : undefined}>
                      <HistoryMessageBubble msg={msg} />
                    </div>
                  ))}
                  {hasMoreHistory && (
                    <button
                      className="hist-load-more"
                      type="button"
                      onClick={loadMoreHistory}
                    >
                      加载更多 ({historyData!.messages.length}/{historyData!.total})
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <SearchOverlay
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelectDate={handleSearchSelectDate}
      />
    </>
  )
}
